import { useMatcapTexture, Center, OrbitControls, Text3D, Float, Sparkles } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useEffect, useRef, useMemo } from 'react'
import * as THREE from 'three'

const torusGeometry = new THREE.TorusGeometry(1, 0.6, 16, 36)
const material = new THREE.MeshMatcapMaterial()
const DONUT_COUNT = 100

export default function Experience()
{
    const donutsRef = useRef()
    const textRef = useRef()

    const [ matcapTexture ] = useMatcapTexture('2E763A_78A0B7_B3D1CF_14F209', 256)

    // Pre-calculate random data for donuts (stable across re-renders)
    const donutsData = useMemo(() => {
        const data = []
        for (let i = 0; i < DONUT_COUNT; i++) {
            data.push({
                position: new THREE.Vector3(
                    (Math.random() - 0.5) * 10,
                    (Math.random() - 0.5) * 10,
                    (Math.random() - 0.5) * 10
                ),
                scale: 0.2 + Math.random() * 0.2,
                rotation: new THREE.Euler(
                    Math.random() * Math.PI,
                    Math.random() * Math.PI,
                    0
                )
            })
        }
        return data
    }, [])

    useEffect(() => {
        matcapTexture.colorSpace = THREE.SRGBColorSpace
        matcapTexture.needsUpdate = true

        material.matcap = matcapTexture
        material.needsUpdate = true

        // Set initial transforms for instanced mesh
        if (donutsRef.current) {
            const tempMatrix = new THREE.Matrix4()
            const tempQuaternion = new THREE.Quaternion()

            donutsData.forEach((donut, i) => {
                tempQuaternion.setFromEuler(donut.rotation)
                tempMatrix.compose(donut.position, tempQuaternion, new THREE.Vector3(donut.scale, donut.scale, donut.scale))
                donutsRef.current.setMatrixAt(i, tempMatrix)
            })
            donutsRef.current.instanceMatrix.needsUpdate = true
        }
    }, [matcapTexture, donutsData])

    useFrame((state, delta) => {
        // Animate instanced donuts rotation
        if (donutsRef.current) {
            const tempMatrix = new THREE.Matrix4()
            const tempPosition = new THREE.Vector3()
            const tempQuaternion = new THREE.Quaternion()
            const tempScale = new THREE.Vector3()
            const rotationDelta = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), delta * 0.3)

            for (let i = 0; i < DONUT_COUNT; i++) {
                donutsRef.current.getMatrixAt(i, tempMatrix)
                tempMatrix.decompose(tempPosition, tempQuaternion, tempScale)
                tempQuaternion.multiply(rotationDelta)
                tempMatrix.compose(tempPosition, tempQuaternion, tempScale)
                donutsRef.current.setMatrixAt(i, tempMatrix)
            }
            donutsRef.current.instanceMatrix.needsUpdate = true
        }
    })

    return <>

        {/* OrbitControls - Z axis limited, pan disabled */}
        <OrbitControls
            makeDefault
            enablePan={false}
            minPolarAngle={Math.PI * 0.25}
            maxPolarAngle={Math.PI * 0.75}
            minAzimuthAngle={-Math.PI * 0.5}
            maxAzimuthAngle={Math.PI * 0.5}
            minDistance={3}
            maxDistance={7}
        />

        {/* Sparkles effect */}
        <Sparkles
            count={200}
            scale={12}
            size={3}
            speed={0.4}
            color="#2E763A"
        />

        {/* Floating 3D text */}
        <Float
            speed={2}
            rotationIntensity={0.2}
            floatIntensity={0.5}
        >
            <Center>
                <Text3D
                    ref={textRef}
                    font='./fonts/helvetiker_regular.typeface.json'
                    size={ 0.75 }
                    height={ 0.2 }
                    curveSegments={ 12 }
                    bevelEnabled
                    bevelThickness={ 0.02 }
                    bevelSize={ 0.02 }
                    bevelSegments={ 5 }
                    bevelOffset={ 0 }
                >
                    Unfor Dev
                    <meshMatcapMaterial matcap={ matcapTexture } />
                </Text3D>
            </Center>
        </Float>

        {/* Instanced donuts - optimized: 1 draw call instead of 100 */}
        <instancedMesh
            ref={donutsRef}
            args={[torusGeometry, material, DONUT_COUNT]}
            frustumCulled={false}
        />

    </>
}